import React, { useState } from 'react';

// PRD-008 §7 — reason-field gate modal. Uncomfortable on purpose: the
// operator commits a reason to the permanent record before the action
// proceeds. Tone is matter-of-fact, not punitive — this creates
// provenance, it doesn't ask permission.
const SUGGESTED_REASONS_KEY = 'cowork:operatorReasons';

function recentReasons() {
  try {
    const raw = sessionStorage.getItem(SUGGESTED_REASONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function rememberReason(reason) {
  try {
    const list = recentReasons();
    const next = [reason, ...list.filter((r) => r !== reason)].slice(0, 5);
    sessionStorage.setItem(SUGGESTED_REASONS_KEY, JSON.stringify(next));
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

const MIN_LENGTH = 10;

export default function ReasonModal({
  open,
  title,
  description,
  confirmLabel = 'Continue',
  onConfirm,
  onCancel,
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;
  const tooShort = reason.trim().length < MIN_LENGTH;

  const handleConfirm = async () => {
    if (tooShort || submitting) return;
    setSubmitting(true);
    try {
      rememberReason(reason.trim());
      await onConfirm(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-gray-900/50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Reason
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Your reason will be recorded in the platform audit log and may be reviewed."
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </label>
          {recentReasons().length > 0 && (
            <div className="space-y-1">
              <span className="text-xs text-gray-600 uppercase tracking-wide">Recent</span>
              <div className="flex flex-wrap gap-2">
                {recentReasons().map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className="px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-gray-600">
            Minimum {MIN_LENGTH} characters. {reason.trim().length}/{MIN_LENGTH}
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={tooShort || submitting}
            className="px-4 py-1.5 text-sm font-medium text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-50 rounded-md"
          >
            {submitting ? 'Submitting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
